/**
 * Upload files straight to S3 through the presigned PUT URLs the server handed
 * out, and report which ones actually landed.
 *
 * Used by the sports and graduation import modals (djobs/_photography_admin and
 * _photography_nonadmin), which used to carry four hand-kept copies of this loop.
 * Those copies counted a failed PUT but then sent EVERY presigned key to the
 * import worker anyway, so a photo that never reached S3 came back as
 * "Error: Aws::S3::Errors::NoSuchKey" and the real reason (a 403, a dropped
 * connection) was lost. Only `uploadedKeys` may be handed to the worker.
 *
 * @param {File[]} files       the files the operator selected
 * @param {{filename: string, key: string, url: string}[]} uploads  the presign response
 * @param {object}  [opts]
 * @param {Function} [opts.fetchImpl]   injectable for tests; defaults to fetch
 * @param {number}   [opts.concurrency] parallel PUTs (default 5)
 * @param {Function} [opts.onProgress]  (done, failed) after every file
 * @returns {Promise<{uploadedKeys: string[], failures: {filename: string, reason: string}[]}>}
 */
export default async function uploadToPresignedUrls(files, uploads, opts = {}) {
  const fetchImpl = opts.fetchImpl || ((...args) => fetch(...args));
  const concurrency = opts.concurrency || 5;
  const onProgress = opts.onProgress || (() => {});

  const byName = {};
  (uploads || []).forEach((u) => { byName[u.filename] = u; });

  const uploadedKeys = [];
  const failures = [];
  let cursor = 0;

  const report = () => onProgress(uploadedKeys.length + failures.length, failures.length);

  async function worker() {
    while (cursor < files.length) {
      const file = files[cursor];
      cursor += 1;
      const spec = byName[file.name];
      if (!spec) {
        failures.push({ filename: file.name, reason: 'no upload URL from the server' });
      } else {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchImpl(spec.url, {
            method: 'PUT',
            headers: { 'Content-Type': 'image/jpeg' },
            body: file,
          });
          if (response.ok) {
            uploadedKeys.push(spec.key);
          } else {
            failures.push({ filename: file.name, reason: `S3 refused the upload (HTTP ${response.status})` });
          }
        } catch (e) {
          failures.push({ filename: file.name, reason: `network error (${e.message})` });
        }
      }
      report();
    }
  }

  const workers = [];
  for (let w = 0; w < Math.min(concurrency, files.length); w += 1) workers.push(worker());
  await Promise.all(workers);

  return { uploadedKeys, failures };
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The failed uploads as a red list for the modal's final summary -- by name, so
 * the operator knows exactly which photos to select again. Empty string when
 * nothing failed.
 */
export function uploadFailuresHtml(failures) {
  if (!failures || failures.length === 0) return '';
  const rows = failures
    .map((f) => `<div>${escapeHtml(`${f.filename} → upload failed: ${f.reason}`)}</div>`)
    .join('');
  return '<div style="color:red;margin-top:8px;font-size:12px;text-align:left;">'
    + `<strong>${failures.length} file(s) did not upload and were NOT processed — select them again:</strong>`
    + `${rows}</div>`;
}
